/**
 * Hit-testing for the Confluence Databases table scroll container only (`@databases/selections`), not
 * WebGL, not an HTML canvas element. The attribute `data-canvas-target="database-scrollable-inner-container"`
 * is a platform event-target id (see `CanvasEventTarget` in `@canvas/input-types`, value
 * `database-scrollable-inner-container`); it does not refer to a drawing canvas.
 *
 * Detects **classic** (layout-reserving) native scrollbars from the layout delta
 * `offsetWidth - clientWidth` / `offsetHeight - clientHeight` minus summed border widths on each
 * axis (the raw delta includes borders). **Overlay** scrollbars (e.g. some macOS settings) often
 * leave no gutter, so this returns false and selection hit-testing is unchanged for those cases.
 */
const DATABASE_SCROLLABLE_INNER_SELECTOR =
  '[data-canvas-target="database-scrollable-inner-container"], #scrollable-inner-container';

function isPointerOnNativeScrollbar(
  container: HTMLElement,
  clientX: number,
  clientY: number,
): boolean {
  const style = getComputedStyle(container);
  const isRtl = style.direction === 'rtl';
  const rect = container.getBoundingClientRect();
  const borderHorizontal =
    (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
  const borderVertical =
    (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  const verticalGutter = container.offsetWidth - container.clientWidth - borderHorizontal;
  const horizontalGutter =
    container.offsetHeight - container.clientHeight - borderVertical;

  if (
    verticalGutter > 0 &&
    container.scrollHeight > container.clientHeight &&
    (isRtl
      ? clientX >= rect.left && clientX <= rect.left + verticalGutter
      : clientX >= rect.right - verticalGutter && clientX <= rect.right)
  ) {
    return true;
  }

  if (
    horizontalGutter > 0 &&
    container.scrollWidth > container.clientWidth &&
    clientY >= rect.bottom - horizontalGutter &&
    clientY <= rect.bottom
  ) {
    return true;
  }

  return false;
}

/**
 * Returns whether viewport coordinates fall on a native **classic** scrollbar strip of the
 * Databases table scrollable inner element. When true, table hit testing should not classify the
 * pointer as `{ type: 'outside' }` (which would `deselectAll`), so the browser can handle
 * scrolling without clearing selection.
 */
export function isPointerOnDatabaseScrollableNativeScrollbar(
  fromElement: Element,
  clientX: number,
  clientY: number,
): boolean {
  const container = fromElement.closest(DATABASE_SCROLLABLE_INNER_SELECTOR);

  if (!(container instanceof HTMLElement)) {
    // Hit is outside the Databases #scrollable-inner (or a non-HTMLElement node).
    return false;
  }

  return isPointerOnNativeScrollbar(container, clientX, clientY);
}
